import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CatalogueApiService,
  RsApiService,
  RS_BASE_URL,
} from '@angular-workspace/services';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-gateway-data',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gateway-data.component.html',
  styleUrl: './gateway-data.component.scss',
})
export class GatewayDataComponent implements OnInit, OnDestroy {
  @Input() resourceItemData: {
    id?: string;
    name?: string;
    label?: string;
    apiServerBaseUrl?: string;
    /** Field names from item `resourceServer.searchValues` (e.g. latitude, longitude). */
    searchValues?: readonly string[];
    placeholderValues?: Record<string, string | number>;
  } | null = null;
  @Input() dataModel: 'model1' | 'model2' = 'model1';
  @Input() accessTypes: readonly string[] | null = null;

  @Output() closeRequested = new EventEmitter<void>();

  allData: unknown[] = [];
  displayData = '';
  isLoading = false;
  errorMessage = '';

  /** One input per catalogue searchValue; `q` is built as key==value;key2==value2. */
  searchFields: string[] = [];
  queryValues: Record<string, string> = {};

  startDate = '';
  endDate = '';
  minDateStr = '';
  maxDateStr = '';

  private abortController: AbortController | null = null;

  private rsApiService = inject(RsApiService);
  private catalogueApiService = inject(CatalogueApiService);
  private rsBaseUrl = inject(RS_BASE_URL);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    const today = new Date();
    this.maxDateStr = this.formatDate(today);
    const minDate = new Date(today.getFullYear() - 10, 0, 1);
    this.minDateStr = this.formatDate(minDate);
  }

  async ngOnInit(): Promise<void> {
    const id = this.resourceItemData?.id;
    if (!id) {
      this.errorMessage = 'Dataset ID is not available.';
      return;
    }
    this.initSearchFields();
    if (this.dataModel === 'model1' && this.areSearchFieldsFilled()) {
      await this.fetchAllEntityData();
    }
  }

  get hasSearchFields(): boolean {
    return this.searchFields.length > 0;
  }

  get searchFieldsHint(): string {
    if (!this.searchFields.length) return 'entity data';
    const labels = this.searchFields.map((k) => this.fieldLabel(k).toLowerCase());
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
  }

  get isAttributeSearchDisabled(): boolean {
    return this.isLoading || (this.hasSearchFields && !this.areSearchFieldsFilled());
  }

  fieldLabel(key: string): string {
    const last = key.split('.').pop() ?? key;
    return last
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  fieldPlaceholder(key: string): string {
    const example = String(this.resourceItemData?.placeholderValues?.[key] ?? '').trim();
    if (example) return `e.g. ${example}`;
    return `Enter ${this.fieldLabel(key).toLowerCase()}`;
  }

  /** Phone (or tel) attribute fields: digits only, exactly 10. */
  isPhoneField(key: string): boolean {
    const last = (key.split('.').pop() ?? key).toLowerCase().replace(/[_-]+/g, '');
    return last === 'phone' || last === 'phonenumber' || last === 'mobile' || last === 'mobilenumber' || last === 'tel';
  }

  onFieldInput(field: string, event: Event): void {
    if (!this.isPhoneField(field)) return;
    const input = event.target as HTMLInputElement;
    const digits = (input.value ?? '').replace(/\D/g, '').slice(0, 10);
    input.value = digits;
    this.queryValues[field] = digits;
  }

  private initSearchFields(): void {
    const fromItem = (this.resourceItemData?.searchValues ?? [])
      .map((v) => String(v ?? '').trim())
      .filter((v) => v.length > 0);
    this.searchFields = [...new Set(fromItem)];
    this.queryValues = {};
    for (const key of this.searchFields) {
      this.queryValues[key] = '';
    }
  }

  private areSearchFieldsFilled(): boolean {
    if (!this.searchFields.length) return true;
    return this.searchFields.every((key) => {
      const value = this.queryValues[key]?.trim() ?? '';
      if (this.isPhoneField(key)) return /^\d{10}$/.test(value);
      return value.length > 0;
    });
  }

  ngOnDestroy(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private updateDisplayData(): void {
    this.displayData = JSON.stringify(this.allData, null, 2);
  }

  downloadAllData(): void {
    const dataStr = JSON.stringify(this.allData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.resourceItemData?.name || this.resourceItemData?.label || 'gateway-data'}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  closeModal(): void {
    this.closeRequested.emit();
  }

  searchAttributeData(): void {
    if (!this.areSearchFieldsFilled()) {
      this.errorMessage = `Please enter ${this.searchFieldsHint}`;
      return;
    }
    void this.fetchAllEntityData();
  }

  resetAttributeQuery(): void {
    for (const key of this.searchFields) {
      this.queryValues[key] = '';
    }
    this.errorMessage = '';
    this.allData = [];
    this.displayData = '';
  }

  searchTemporalData(): void {
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select both start and end dates';
      return;
    }
    if (this.startDate > this.endDate) {
      this.errorMessage = 'Start date must be before end date';
      return;
    }
    void this.fetchTemporalData();
  }

  resetDates(): void {
    this.startDate = '';
    this.endDate = '';
    this.errorMessage = '';
    this.allData = [];
    this.displayData = '';
  }

  private extractEntries(response: unknown): unknown[] {
    if (response == null) return [];
    if (Array.isArray(response)) {
      if (response.length === 1 && Array.isArray(response[0])) {
        return response[0] as unknown[];
      }
      return response;
    }
    if (typeof response !== 'object') return [];

    const body = response as Record<string, unknown>;
    const nested =
      body['data'] != null &&
      typeof body['data'] === 'object' &&
      !Array.isArray(body['data'])
        ? (body['data'] as Record<string, unknown>)
        : null;

    const pickArray = (value: unknown): unknown[] | null => {
      if (!Array.isArray(value)) return null;
      if (value.length === 1 && Array.isArray(value[0])) {
        return value[0] as unknown[];
      }
      return value;
    };

    const arrayCandidates = [
      nested?.['results'],
      nested?.['result'],
      body['results'],
      body['result'],
      body['data'],
    ];
    for (const candidate of arrayCandidates) {
      const arr = pickArray(candidate);
      if (arr && arr.length > 0) return arr;
    }
    for (const candidate of arrayCandidates) {
      const arr = pickArray(candidate);
      if (arr) return arr;
    }

    for (const candidate of [nested?.['result'], body['result'], nested, body]) {
      if (candidate != null && typeof candidate === 'object' && !Array.isArray(candidate)) {
        const obj = candidate as Record<string, unknown>;
        const nestedResults = pickArray(obj['results']) ?? pickArray(obj['result']);
        if (nestedResults && nestedResults.length > 0) return nestedResults;
        if ('id' in obj || '@id' in obj) return [candidate];
      }
    }

    return [];
  }

  private resolveTotalPages(response: unknown): number {
    if (response == null || typeof response !== 'object') return 1;
    const body = response as Record<string, unknown>;
    const nested =
      body['data'] != null && typeof body['data'] === 'object'
        ? (body['data'] as Record<string, unknown>)
        : null;
    const info =
      (body['paginationInfo'] as Record<string, unknown> | undefined) ??
      (nested?.['paginationInfo'] as Record<string, unknown> | undefined);
    const totalPages = info?.['totalPages'];
    return typeof totalPages === 'number' && totalPages > 0 ? totalPages : 1;
  }

  /** Curl: q=latitude==18.624;longitude==73.076 (keys from searchValues, one value each). */
  private buildAttributeQ(): string | null {
    if (!this.areSearchFieldsFilled() || !this.searchFields.length) return null;
    return this.searchFields
      .map((key) => `${key}==${this.queryValues[key].trim()}`)
      .join(';');
  }

  private getGatewayOptions(): { baseUrl: string; q?: string } | undefined {
    const envGateway =
      ((environment as { gatewayApiPath?: string }).gatewayApiPath ?? '').trim();
    const fromResource = (this.resourceItemData?.apiServerBaseUrl ?? '').trim();
    let gatewayPath = (envGateway || fromResource).replace(/\/+$/, '');
    if (!gatewayPath) return undefined;
    if (/\/ngsi-ld\/v1\/entities$/i.test(gatewayPath)) {
      gatewayPath = gatewayPath.replace(/\/ngsi-ld\/v1\/entities$/i, '');
    } else if (!/\/rsp$/i.test(gatewayPath)) {
      gatewayPath = `${gatewayPath}/rsp`;
    }
    const q = this.buildAttributeQ();
    return q ? { baseUrl: gatewayPath, q } : { baseUrl: gatewayPath };
  }

  private async fetchAllEntityData(): Promise<void> {
    if (this.isLoading) return;
    const id = this.resourceItemData?.id?.trim();
    if (!id || id === 'dataset') {
      this.errorMessage = 'Dataset ID is not available.';
      return;
    }
    if (this.hasSearchFields && !this.buildAttributeQ()) {
      this.errorMessage = `Please enter ${this.searchFieldsHint}`;
      return;
    }
    const options = this.getGatewayOptions();
    if (!options) {
      this.errorMessage = 'Gateway API URL is not configured.';
      return;
    }
    try {
      this.isLoading = true;
      this.errorMessage = '';

      const firstResponse = await this.rsApiService.entitiesNgsiLg(id, undefined, options);
      let entries = this.extractEntries(firstResponse);
      const totalPages = this.resolveTotalPages(firstResponse);

      for (let page = 2; page <= totalPages; page++) {
        const pageResponse = await this.rsApiService.entitiesNgsiLg(id, page, options);
        entries = entries.concat(this.extractEntries(pageResponse));
      }

      this.allData = entries;
      this.isLoading = false;
      this.cdr.detectChanges();

      if (entries.length === 0 && firstResponse != null) {
        this.allData = [firstResponse];
      }
      this.updateDisplayData();
      if (this.allData.length === 0) {
        this.errorMessage = 'No entity data found.';
      }
      this.cdr.markForCheck();
    } catch {
      this.errorMessage = 'Failed to fetch entity data. Please try again.';
      this.allData = [];
      this.displayData = '';
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private async fetchTemporalData(): Promise<void> {
    if (this.isLoading) return;
    const id = this.resourceItemData?.id;
    if (!id) {
      this.errorMessage = 'Dataset ID is not available.';
      return;
    }
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const timeFrom = `${this.startDate}T00:00:00Z`;
    const timeTo = `${this.endDate}T23:59:59Z`;
    try {
      this.isLoading = true;
      this.errorMessage = '';
      const response = await this.catalogueApiService.getTemporalEntities(
        id,
        timeFrom,
        timeTo,
        this.rsBaseUrl,
        { signal },
      );
      let entries: unknown[] = [];
      if (response?.result && Array.isArray(response.result)) {
        entries = response.result;
      } else if (Array.isArray(response)) {
        entries = response;
      } else if (response?.data && Array.isArray(response.data)) {
        entries = response.data;
      } else if (response?.results && Array.isArray(response.results)) {
        entries = response.results;
      }
      this.allData = entries;
      this.updateDisplayData();
      if (entries.length === 0) {
        this.errorMessage = 'No data found for the selected date range';
      }
    } catch (error: unknown) {
      const err = error as { name?: string };
      if (err?.name === 'AbortError') {
        return;
      }
      this.errorMessage = 'Failed to fetch temporal data. Please try again.';
      this.allData = [];
      this.displayData = '';
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
